/** Lookup catalogue items from bound engine data (after bindEngineData). */
import {
  CATALOGUE,
  PRINTS,
  CUTS,
  LAMINATIONS,
  FINISHINGS,
  ACCESSORIES,
  SERVICES_CAT,
  STANDS,
  FINISHED_PRODUCTS,
} from "../../data";
import { num } from '../core/core';
import type { CatalogueItem } from './provider';

/**
 * Number, or null for an empty value.
 * @param v The value to convert.
 * @returns The number, or null when empty.
 */
const numOrNull = (v: unknown): number | null =>
  v === '' || v == null ? null : num(v);

/**
 * Resolves a catalogue ref against the bound referential.
 * @param ref Item or component reference.
 * @returns The normalized item, or undefined when the ref is unknown.
 */
export function getItemBound(ref: string): CatalogueItem | undefined {
  const r = ref.trim();
  if (!r) return undefined;
  const materialEntry = CATALOGUE.find((x) => x.ref === r);
  if (materialEntry) {
    return {
      ref: materialEntry.ref,
      type: 'material',
      category: materialEntry.category,
      itemShort: materialEntry.name,
      fullName: materialEntry.fullName,
      pvM2: materialEntry.pv,
      minUnitPrice: materialEntry.min_unit_price,
      minFlatFee: materialEntry.min_flat_fee,
      standardPrint: materialEntry.standardPrint,
      trigger_key: null,
      active: true,
    };
  }
  const print = PRINTS.find((x) => x.code === r);
  if (print) {
    return {
      ref: print.code,
      type: 'print',
      category: '',
      itemShort: print.label,
      fullName: print.label,
      pvM2: print.pv,
      minUnitPrice: numOrNull(print.min_unit_price),
      minFlatFee: null,
      standardPrint: null,
      trigger_key: null,
      active: true,
    };
  }
  const cut = CUTS.find((x) => x.code === r);
  if (cut) {
    return {
      ref: cut.code,
      type: 'cut',
      category: '',
      itemShort: cut.label,
      fullName: cut.fullLabel ?? cut.label,
      pvM2: cut.pv,
      minUnitPrice: numOrNull(cut.min_unit_price),
      minFlatFee: numOrNull(cut.min_flat_fee),
      standardPrint: null,
      trigger_key: cut.trigger ?? null,
      active: true,
    };
  }
  const lamination = LAMINATIONS.find((x) => x.code === r);
  if (lamination) {
    return {
      ref: lamination.code,
      type: 'lamination',
      category: '',
      itemShort: lamination.label,
      fullName: lamination.label,
      pvM2: lamination.pv,
      minUnitPrice: numOrNull(lamination.min_unit_price),
      minFlatFee: null,
      standardPrint: null,
      trigger_key: null,
      active: true,
    };
  }
  const finishing = FINISHINGS.find((x) => x.code === r);
  if (finishing?.code && /^CONF-/.test(finishing.code)) {
    return {
      ref: finishing.code,
      type: 'finishing',
      category: '',
      itemShort: finishing.label ?? finishing.code,
      fullName: finishing.fullLabel ?? finishing.label ?? finishing.code,
      pvM2: finishing.pv,
      minUnitPrice: null,
      minFlatFee: null,
      standardPrint: null,
      trigger_key: finishing.trigger ?? null,
      active: true,
    };
  }
  const acc = ACCESSORIES.find((x) => x.ref === r);
  if (acc) {
    return {
      ref: acc.ref,
      type: 'accessory',
      category: '',
      itemShort: acc.name,
      fullName: acc.name,
      pvM2: acc.pu,
      minUnitPrice: null,
      minFlatFee: null,
      standardPrint: null,
      trigger_key: null,
      active: true,
    };
  }
  const svc = SERVICES_CAT.find((x) => x.code === r);
  if (svc) {
    return {
      ref: svc.code,
      type: 'service',
      category: '',
      itemShort: svc.label,
      fullName: svc.label,
      pvM2: svc.pv,
      minUnitPrice: null,
      minFlatFee: null,
      standardPrint: null,
      trigger_key: null,
      active: true,
    };
  }
  const stand = STANDS.find((x) => x.ref === r);
  if (stand) {
    return {
      ref: stand.ref,
      type: 'stand',
      category: stand.category,
      itemShort: stand.name,
      fullName: stand.name,
      pvM2: null,
      minUnitPrice: null,
      minFlatFee: null,
      standardPrint: null,
      trigger_key: null,
      active: true,
    };
  }
  const finished = FINISHED_PRODUCTS.find((x) => x.ref === r);
  if (finished) {
    return {
      ref: finished.ref,
      type: 'finished_product',
      category: finished.category,
      itemShort: finished.name,
      fullName: finished.name,
      pvM2: null,
      minUnitPrice: null,
      minFlatFee: null,
      standardPrint: null,
      trigger_key: null,
      active: true,
    };
  }
  return undefined;
}
